import React, { useCallback, useState } from 'react';
import { alpha, Box, CircularProgress, Collapse, IconButton, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { sanitizeHtml } from 'tools/safeHtml';
import { conciergeAPI } from 'api/concierge';
import useBundleTranslation from 'i18n';
import IconMi from 'components/common/icon/IconMi';

// Matches the server-side answer cache, so re-opening the panel does not ask again.
const PANEL_INSIGHT_STALE_MS = 12 * 60 * 60 * 1000;
// The viewer's own choice, kept across panel opens and reports; storage may be unavailable.
const COLLAPSED_STORAGE_KEY = 'concierge.panelInsight.collapsed';

function readCollapsed(): boolean {
    try {
        return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === 'Y';
    } catch {
        return false;
    }
}

// AI_Insights.md §9.5 / prototype Viewer-concierge.html (design-prototypes 9a2037a407): the report's
// first applicable AI Insight at the top of the panel. On-demand generation can take ~25 s, so the
// section shows a loading line until the engine answers; no insight renders nothing.
export default function PanelInsightCard({ elementId, insightId }: { elementId: number; insightId: number | null }) {
    const { t } = useBundleTranslation('app/concierge/widget');
    const { t: tCard } = useBundleTranslation('components/common/issue-card');
    const [collapsed, setCollapsed] = useState(readCollapsed);
    const toggleCollapsed = useCallback(() => {
        setCollapsed((current) => {
            const next = !current;
            try {
                window.localStorage.setItem(COLLAPSED_STORAGE_KEY, next ? 'Y' : 'N');
            } catch {
                // keep the in-memory state only
            }
            return next;
        });
    }, []);
    const { data: insight, isLoading } = useQuery({
        queryKey: ['conciergePanelInsight', elementId, insightId],
        queryFn: () => conciergeAPI.getPanelInsight(elementId, insightId),
        // An empty answer means every insight failed or was suppressed; the server does not cache that
        // either, so the next panel open asks again.
        staleTime: (query) => (query.state.data === null ? 0 : PANEL_INSIGHT_STALE_MS),
        retry: false,
    });
    if (!isLoading && !insight) {
        return null;
    }

    return (
        <Box
            sx={{ flexShrink: 0, p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
            data-test="concierge_panel_insight"
        >
            <Stack
                direction="row"
                alignItems="center"
                spacing={0.75}
                onClick={toggleCollapsed}
                sx={{
                    color: 'primary.main',
                    fontSize: '12px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover .panel-insight-label': { opacity: 0.65 },
                }}
                data-test="concierge_panel_insight_header"
            >
                <IconMi icon="sparkles" fontSize="14" />
                <Box
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'baseline',
                        columnGap: 0.75,
                    }}
                >
                    <Box component="span" className="panel-insight-label">
                        {t('panel_insight.label')}
                    </Box>
                    <Box
                        component="span"
                        sx={{
                            color: 'text.secondary',
                            fontSize: '11px',
                            fontWeight: 400,
                            textTransform: 'none',
                            letterSpacing: 0,
                        }}
                        data-test="concierge_panel_insight_caption"
                    >
                        {t('panel_insight.generated_by')}
                    </Box>
                </Box>
                <IconButton
                    size="small"
                    aria-expanded={!collapsed}
                    aria-label={collapsed ? tCard('expand') : tCard('collapse')}
                    onClick={(event) => {
                        event.stopPropagation();
                        toggleCollapsed();
                    }}
                    sx={{ p: '6px', flexShrink: 0, my: '-6px' }}
                    data-test="concierge_panel_insight_toggle"
                >
                    <IconMi icon={collapsed ? 'chevron-down' : 'chevron-up'} sx={{ fontSize: '16px' }} />
                </IconButton>
            </Stack>
            <Collapse in={!collapsed}>
                <Box sx={{ pt: 1 }}>
                    {isLoading || !insight ? (
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ color: 'text.secondary' }}
                            data-test="concierge_panel_insight_loading"
                        >
                            <CircularProgress size={14} />
                            <Typography sx={{ color: 'inherit' }}>{t('panel_insight.loading')}</Typography>
                        </Stack>
                    ) : (
                        <Box
                            sx={(theme) => {
                                const tone = insight.significant
                                    ? theme.palette.warning.main
                                    : theme.palette.primary.main;
                                return {
                                    bgcolor: alpha(tone, 0.04),
                                    border: `1px solid ${alpha(tone, 0.24)}`,
                                    borderLeft: `3px solid ${tone}`,
                                    borderRadius: '6px',
                                    px: 2,
                                    py: 1.75,
                                };
                            }}
                            data-test="concierge_panel_insight_card"
                            data-insight-kind={insight.kind}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                flexWrap="wrap"
                                useFlexGap
                                spacing={1}
                                sx={{ mb: 0.75 }}
                            >
                                <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>{insight.name}</Typography>
                                {insight.significant && (
                                    <Box
                                        component="span"
                                        sx={(theme) => ({
                                            px: '7px',
                                            py: '2px',
                                            bgcolor: alpha(theme.palette.warning.main, 0.32),
                                            fontSize: '10px',
                                            fontWeight: 700,
                                            letterSpacing: '0.3px',
                                            textTransform: 'uppercase',
                                        })}
                                        data-test="concierge_panel_insight_significant"
                                    >
                                        {t('panel_insight.significant')}
                                    </Box>
                                )}
                            </Stack>
                            <Box
                                sx={{
                                    fontSize: '13px',
                                    lineHeight: 1.6,
                                    color: 'text.secondary',
                                    overflowWrap: 'anywhere',
                                    '& p': { m: 0, mb: 1 },
                                    '& p:last-child': { mb: 0 },
                                    '& ul, & ol': { m: 0, mb: 1, pl: 2.5 },
                                    '& h1, & h2, & h3, & h4': { fontSize: '13px', fontWeight: 700, m: 0, mb: 0.5 },
                                }}
                                dangerouslySetInnerHTML={{ __html: sanitizeHtml(insight.html) }}
                                data-test="concierge_panel_insight_text"
                            />
                            {insight.sources.length > 0 && (
                                <Typography
                                    sx={{ mt: 1.25, fontSize: '11px', color: 'text.secondary' }}
                                    data-test="concierge_panel_insight_sources"
                                >
                                    {t('panel_insight.sources', { sources: insight.sources.join(' · ') })}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
}
